import './MobileViewport.scss';

import {LatLng} from '../../react-app-env';
import React, {useEffect} from 'react';
import Sidebar from '../sidebar/Sidebar';
import MapView from '../map/mapview/MapView';
import Icon, {IconSizes} from '../icon/Icon';
import {Events, EventSubscription, EventType} from '../../events/Events';
import {delay} from '../../utils/Timing';
import {noop} from '../../utils/Utils';
import {useSelectedPoi} from '../context/SelectedPoiContext';

export interface MobileViewportProps {
  readonly googleMapsApiKey: string;
}

enum ViewMode {
  TRIP,
  MAP
}

enum MapSize {
  FULLSCREEN,
  SPLIT
}

/**
 * Waits 250ms until Map component has its final viewport size, then re-centers the location.
 *
 * @param {LatLng} location The location to center in the MapView.
 */
function triggerDelayedCenterInMap(location: LatLng) {
  delay(250, () => {
    Events.publish(EventType.CENTER_IN_MAP, location);
  });
}

/**
 * Fernrausch main viewport for mobile devices.
 */
function MobileViewport({ googleMapsApiKey }: MobileViewportProps) {
  const selectedPoi = useSelectedPoi();
  const [viewMode, setViewMode] = React.useState<ViewMode>(ViewMode.TRIP);
  const [mapSize, setMapSize] = React.useState<MapSize>(MapSize.FULLSCREEN);

  useEffect(() => {
    /**
     * Make sure the map is visible if a location is being centered in the MapView.
     */
    function watchCenterInMap(): EventSubscription {
      return Events.subscribe(EventType.CENTER_IN_MAP, (location) => {
        if (viewMode === ViewMode.TRIP) {
          setViewMode(ViewMode.MAP);
          triggerDelayedCenterInMap(location);
        }
      });
    }

    /**
     * Ensure the MapView isn't full-screen if a POI's detailed are being shown.
     */
    function watchPoiSelected(): EventSubscription {
      return Events.subscribe(EventType.ON_POI_SELECTED, () => {
        if (viewMode === ViewMode.MAP && mapSize === MapSize.FULLSCREEN) {
          setViewMode(ViewMode.TRIP);
        }
      });
    }

    const centerInMapSubscription = watchCenterInMap();
    const poiSelectedSubscription = watchPoiSelected();

    // Clean up after ourselves
    return () => {
      centerInMapSubscription.unsubscribe();
      poiSelectedSubscription.unsubscribe();
    };
  }, [viewMode, mapSize, selectedPoi]);

  let viewModeClass = 'fr-mobile-splitview__';
  if (viewMode === ViewMode.TRIP) {
    viewModeClass += 'trip';
  } else if (mapSize === MapSize.FULLSCREEN) {
    viewModeClass += 'map';
  } else {
    viewModeClass += 'split';
  }

  /**
   * Switches to the specified view mode.
   *
   * @param {ViewMode} targetViewMode View mode to switch to.
   */
  function switchToViewMode(targetViewMode: ViewMode) {
    setViewMode(targetViewMode);

    // If we're opening a map for the first time since selecting a POI, center the map on that POI.
    if (viewMode === ViewMode.TRIP && selectedPoi != null) {
      triggerDelayedCenterInMap(selectedPoi.location);
    }
  }

  return (
      <main className="fr-mobile">
        <div className={'fr-mobile-splitview ' + viewModeClass}>
          <div className="fr-mobile-splitview-trip">
            <Sidebar/>
          </div>
          <div className="fr-mobile-splitview-map">
            <SplitViewButtons viewMode={viewMode}
                              mapSize={mapSize}
                              onViewModeChange={(viewMode) => {
                                switchToViewMode(viewMode);
                              }}
                              onMapSizeChange={(mapViewMode) => {
                                setMapSize(mapViewMode);
                              }}/>
            <MapView googleMapsApiKey={googleMapsApiKey}/>
          </div>
        </div>
      </main>
  );
}

/**
 * Properties of SplitViewButtons.
 */
interface SplitViewButtonsProps {
  readonly viewMode: ViewMode;
  readonly mapSize: MapSize;
  readonly onViewModeChange: (viewMode: ViewMode) => void;
  readonly onMapSizeChange: (mapViewMode: MapSize) => void;
}

/**
 * Buttons to control switching between map/trip/split view in mobile viewport.
 *
 * @param {ViewMode} viewMode Current view mode.
 * @param {string} tripTitle Title of the trip.
 * @param {(viewMode: ViewMode) => void} onViewModeChange Callback to change the view mode.
 * @param {(mapViewMode: MapSize) => void} onMapSizeChange Callback to change the map size.
 */
function SplitViewButtons (
    {
      viewMode,
      mapSize,
      onViewModeChange,
      onMapSizeChange
    }: SplitViewButtonsProps
) {

  function renderButtonsLeft(): React.ReactNode {
    return <div className="fr-mobile-splitview-buttons-left">
      <button onClick={() => Events.publish(EventType.SHOW_TRIP_IN_MAP)}>
        <Icon category={'fa-magnifying-glass'}
              size={IconSizes.MEDIUM}/>
      </button>
      <button onClick={() => noop()}>
        <Icon category={'fa-bullseye'}
              size={IconSizes.MEDIUM}/>
      </button>
      <button onClick={() => noop()}>
        <Icon category={'fa-satellite'}
              size={IconSizes.MEDIUM}/>
      </button>
    </div>;
  }

  function renderButtonsRight(): React.ReactNode {
    return <div className="fr-mobile-splitview-buttons-right">
      {mapSize === MapSize.SPLIT && (
          <button onClick={() => onMapSizeChange(MapSize.FULLSCREEN)}>
            <Icon category={'fa-angle-up'}
                  size={IconSizes.MEDIUM}/>
          </button>
      )}
      {mapSize === MapSize.FULLSCREEN && (
          <button onClick={() => onMapSizeChange(MapSize.SPLIT)}>
            <Icon category={'fa-angle-down'}
                  size={IconSizes.MEDIUM}/>
          </button>
      )}
      <button onClick={() => onViewModeChange(ViewMode.TRIP)}>
        <Icon category={'fa-close'}
              size={IconSizes.MEDIUM}/>
      </button>
    </div>;
  }

  function renderButtonOpenMap(): React.ReactNode {
    if (viewMode === ViewMode.TRIP) {
      return <button className="map"
                     onClick={() => onViewModeChange(ViewMode.MAP)}>
        <Icon category={'fa-map fa-regular'}
              size={IconSizes.MEDIUM}/>
      </button>;
    } else {
      return null;
    }
  }

  return <div className="fr-mobile-splitview-buttons">
    {viewMode === ViewMode.MAP && renderButtonsLeft()}
    {viewMode === ViewMode.MAP && renderButtonsRight()}
    {renderButtonOpenMap()}
  </div>;
}

export default MobileViewport;
